import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { OrderingFlow } from './components/OrderingFlow';
import { StatusScreen } from './components/screens/StatusScreen';
import { track } from './lib/analytics';
import { bwFilterDefList, bwMatrixValues } from './lib/bw';
import { CONTACT } from './lib/contact';

/**
 * Last-resort boundary: a render crash must never leave a customer on a blank
 * page (the pre-2026-07-10 status page did exactly that on a contract
 * mismatch). Shows a friendly retry screen instead.
 */
class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('rpl-print-portal render error', error, info.componentStack);
    track('app_error', { message: String(error?.message ?? error).slice(0, 150) });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="app">
        <main className="app__main">
          <div className="center-screen">
            <h1 className="center-screen__title">Something went wrong</h1>
            <p className="center-screen__body">
              Please refresh the page. If it keeps happening, contact us at{' '}
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> · {CONTACT.phone}
            </p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        </main>
      </div>
    );
  }
}

export function App() {
  return (
    <AppErrorBoundary>
      <>
      {/* B&W style filters (owner 2026-07-31). v1 is plain CSS grayscale();
          v2/v3 are channel mixes only feColorMatrix can express. sRGB
          interpolation is REQUIRED: the bake mixes in gamma space, and the
          SVG default (linearRGB) would preview darker than the print. */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
        <defs>
          {bwFilterDefList().map((d) => (
            <filter key={d.id} id={d.id} colorInterpolationFilters="sRGB">
              <feColorMatrix type="matrix" values={bwMatrixValues(d.style, d.strength)} />
            </filter>
          ))}
        </defs>
      </svg>
      <Routes>
        <Route path="/" element={<OrderingFlow />} />
        <Route path="/status/:orderName" element={<StatusScreen />} />
        {/* Unknown paths fall back into the ordering flow. */}
        <Route path="*" element={<OrderingFlow />} />
      </Routes>
    </>
    </AppErrorBoundary>
  );
}
